/**
 * Cada família de fluxo define:
 * - ordem: a sequência CRONOLÓGICA das situações (não necessariamente a
 *   mesma ordem do list*.json — nos fluxos avulsos o array do JSON não
 *   segue a ordem real do processo, então aqui é explícito e confiável).
 * - nos: para cada situação da ordem, 0, 1 ou 2 nós visuais. Cada nó tem
 *   um `label` e, opcionalmente, `acao: true` quando aquela situação pede
 *   import de documento ou atualização de algum dado no cadastro (usado
 *   pra mostrar o selo "!" na trilha, com `descricaoAcao` no tooltip) —
 *   mapeado a partir das checagens reais em regrasAvancoOS.jsx, não é um
 *   palpite. Uma situação pode virar 2 nós quando representa duas fases
 *   visíveis pro cliente (ex.: "protocolar" e depois "aguardar análise"),
 *   mesmo sendo um único código internamente.
 */

/**
 * Texto de ajuda mostrado num tooltip (ícone "i") na etapa Análise Receita
 * Federal, em todos os tipos — explica como corrigir o cadastro quando a
 * empresa foi marcada como isenta de IE por engano e precisa virar
 * contribuinte. Estruturado em passos (em vez de uma string só) pra dar
 * pra estilizar cada parte no tooltip.
 */
const INFO_ALTERACAO_IE_ARFB = {
  titulo: "Alteração da Inscrição Estadual no cadastro do cliente",
  intro:
    'Caso, no cadastro do cliente, tenha sido informado que a empresa não possui Inscrição Estadual (IE) e seja necessário alterar essa informação para "Sim, possui IE", siga os passos abaixo:',
  passos: [
    'Retroceda a ordem até a situação "Análise Junta Comercial".',
    "Acesse o cadastro do cliente.",
    'Altere o campo "Contribuição Estadual" para "Contribuinte".',
    'No campo "IE", informe o número da Inscrição Estadual da empresa.',
    "Salve as alterações no cadastro.",
    "Após realizar essas alterações, tente avançar novamente a ordem.",
  ],
  outro:
    'Ao tentar avançar a ordem, o sistema irá apresentar uma pergunta confirmando se a empresa possui Inscrição Estadual. Nesse momento, selecione a opção "Sim, possui IE" para prosseguir com o processo.',
};

/** Abertura (tipo 1) e Alteração (tipo 4) — não passam por M-TE nem exigem Procuração ECAC em P-DOC (isso é só da Migração). */
const ABERTURA_ALTERACAO = {
  ordem: [
    "I", "S", "S-N", "P", "P-DOC", "V-CCM", "A-RFB",
    "E-JCM", "A-JCM", "E-CCM", "A-CCM", "E-DOC", "F-DOC", "E-ENF", "F",
  ],
  nos: {
    I: [{ label: "Inicial" }],
    S: [{ label: "Solicitação de Serviço" }],
    "S-N": [],
    P: [{
      label: "Confirmação de Pagamento",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário ter um cliente cadastrado e vinculado à Ordem de Serviço.",
    }],
    "P-DOC": [{ label: "Pendente Documentação" }],
    "V-CCM": [{
      label: "Viabilidade Prefeitura",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário importar o documento de Viabilidade da Prefeitura e, se houver, informar o conselho de classe da empresa.",
    }],
    "A-RFB": [{
      label: "Análise Receita Federal",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário importar o documento FCPS/Protocolo REDESIM.",
    }],
    "E-JCM": [{
      label: "Elaboração Junta Comercial",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário importar o modelo de contrato correspondente (Abertura ou Alteração), conforme o tipo da Ordem de Serviço.",
    }],
    "A-JCM": [{
      label: "Análise Junta Comercial",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário confirmar a Inscrição Estadual (documento e cadastro) e importar o CNPJ, além dos demais documentos exigidos conforme o tipo da Ordem.",
      informacao: INFO_ALTERACAO_IE_ARFB,
    
    }],
    "E-CCM": [{ label: "Elaboração Prefeitura" }],
    "A-CCM": [{
      label: "Análise Prefeitura",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário importar a Inscrição Municipal e a Guia TFE.",
    }],
    "E-DOC": [{ label: "Empresa Constituída" }],
    "F-DOC": [{ label: "Registro Regime Tributário" }],
    "E-ENF": [{ label: "Credenciamento Emissor NF" }],
    F: [{ label: "Finalizado" }],
  },
};

/** Migração de Empresa (tipo 3) — única que passa por M-TE e exige Procuração ECAC já em P-DOC. */
const MIGRACAO = {
  ordem: [
    "I", "S", "S-N", "P", "P-DOC", "M-TE", "V-CCM", "A-RFB",
    "E-JCM", "A-JCM", "E-CCM", "A-CCM", "E-DOC", "F-DOC", "E-ENF", "F",
  ],
  nos: {
    I: [{ label: "Inicial" }],
    S: [{ label: "Solicitação de Serviço" }],
    "S-N": [],
    P: [{
      label: "Confirmação de Pagamento",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário ter um cliente cadastrado e vinculado à Ordem de Serviço.",
    }],
    "P-DOC": [{
      label: "Pendente Documentação",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário importar o documento Procuração ECAC.",
    }],
    "M-TE": [{
      label: "Migração Enquadramento",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário informar a data de migração e importar o documento de Desenquadramento do MEI.",
    }],
    "V-CCM": [{
      label: "Viabilidade Prefeitura",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário importar o documento de Viabilidade da Prefeitura e, se houver, informar o conselho de classe da empresa.",
    }],
    "A-RFB": [{
      label: "Análise Receita Federal",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário importar o documento FCPS/Protocolo REDESIM.",
    }],
    "E-JCM": [{
      label: "Elaboração Junta Comercial",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário importar o modelo de contrato de Distrato correspondente.",
    }],
    "A-JCM": [{
      label: "Análise Junta Comercial",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário confirmar a Inscrição Estadual (documento e cadastro) e importar o CNPJ.",
      informacao: INFO_ALTERACAO_IE_ARFB,
    }],
    "E-CCM": [{ label: "Elaboração Prefeitura" }],
    "A-CCM": [{
      label: "Análise Prefeitura",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário importar a Inscrição Municipal e a Guia TFE, e preencher o número da Inscrição Municipal no cadastro.",
    }],
    "E-DOC": [{ label: "Empresa Constituída" }],
    "F-DOC": [{ label: "Registro Regime Tributário" }],
    "E-ENF": [{ label: "Credenciamento Emissor NF" }],
    F: [{ label: "Finalizado" }],
  },
};

/** Baixa/Encerramento (tipo 5) — ordem e rótulos conforme referência visual. */
const BAIXA_ENCERRAMENTO = {
  ordem: [
    "I", "S", "S-N", "P", "P-DOC", "V-CND", "V-CCM", "M-TE",
    "A-RFB", "E-JCM", "A-JCM", "E-CCM", "A-CCM", "E-DOC", "F-DOC", "E-ENF", "F",
  ],
  nos: {
    I: [{ label: "Inicial" }],
    S: [{ label: "Solicitação de Serviço" }],
    "S-N": [],
    P: [{
      label: "Confirmação de Pagamento",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário ter um cliente cadastrado e vinculado à Ordem de Serviço.",
    }],
    "P-DOC": [{ label: "Pendente Documentação" }],
    "V-CND": [{
      label: "Verificação Débitos",
      //acao: true,
    }],
    "V-CCM": [{
      label: "Viabilidade Prefeitura",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário informar, se houver, o conselho de classe da empresa.",
    }],
    // M-TE não aparece nessa trilha — a checagem de M-TE no código só vale
    // pra Migração (tipo 3), nunca pra Baixa/Encerramento (tipo 5).
    "M-TE": [],
    "A-RFB": [{
      label: "Análise Receita Federal",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário importar o documento FCN.",
    }],
    "E-JCM": [{
      label: "Elaboração Junta Comercial",
      acao: true,
      descricaoAcao: "Nessa etapa é necessário importar o Modelo de Distrato da Empresa.",
    }],
    "A-JCM": [
      { label: "Protocolar Guias e Documentos (Junta Comercial)" },
      {
        label: "Análise Junta Comercial",
        acao: true,
        descricaoAcao: "Nessa etapa é necessário importar o CNPJ e o Contrato de Distrato, além de informar a data de encerramento da empresa.",
        informacao: INFO_ALTERACAO_IE_ARFB,
      },
    ],
    "E-CCM": [
      { label: "Elaboração Prefeitura" },
      { label: "Protocolar Guias e Documentos (Prefeitura)" },
    ],
    "A-CCM": [
      {
        label: "Análise Prefeitura",
        acao: true,
        descricaoAcao: "Nessa etapa é necessário importar a Inscrição Municipal e a Guia TFE.",
      },
      { label: "Disponibilizar Documentos" },
    ],
    "E-DOC": [{ label: "Empresa Baixada / Encerrada" }],
    "E-RBE": [{ label: "Registro Baixa Empresa" }],
    "E-DE": [{ label: "Emissão Declaração Extinção" }],
    F: [{ label: "Finalizado" }],
  },
};

/**
 * Serviços avulsos (contábil, fiscal, RH, admissão, rescisão, férias,
 * dependente, pró-labore) — fluxo bem mais curto. Reaproveitável por todos
 * porque compartilham os mesmos códigos de situação; só o rótulo da fase de
 * elaboração muda conforme o serviço.
 */
function montarFamiliaAvulso(rotuloElaboracao) {
  return {
    ordem: ["I", "S", "S-N", "P", "E-DOC", "F"],
    nos: {
      I: [{ label: "Início" }],
      S: [{ label: "Solicitação de Serviço" }],
      "S-N": [],
      P: [{ label: "Confirmação de Pagamento" }],
      "E-DOC": [{ label: rotuloElaboracao }],
      F: [{ label: "Finalizado" }],
    },
  };
}

/** Serviço de Contabilidade (tipo 2). */
const SERVICO_CONTABILIDADE = {
  ordem: ["I", "S", "S-N", "P", "P-DOC", "V-DOC", "V-ENF", "F"],
  nos: {
    I: [{ label: "Início" }],
    S: [{ label: "Solicitação de Serviço" }],
    "S-N": [],
    P: [{ label: "Confirmação de Pagamento" }],
    "P-DOC": [{ label: "Pendente Documentação" }],
    "V-DOC": [{ label: "Verificação Documentos" }],
    "V-ENF": [{ label: "Verificação Credenciais Emissor NF" }],
    F: [{ label: "Finalizado" }],
  },
};

const FAMILIAS_POR_TIPO = {
  1: ABERTURA_ALTERACAO,
  4: ABERTURA_ALTERACAO,
  3: MIGRACAO,
  5: BAIXA_ENCERRAMENTO,
  2: SERVICO_CONTABILIDADE,
  101: montarFamiliaAvulso("Elaborando serviço contábil"),
  102: montarFamiliaAvulso("Elaborando serviço fiscal"),
  103: montarFamiliaAvulso("Elaborando serviço de RH"),
  106: montarFamiliaAvulso("Disponibilizar Documentos"),
  10301: montarFamiliaAvulso("Elaborando admissão"),
  10302: montarFamiliaAvulso("Elaborando rescisão"),
  10304: montarFamiliaAvulso("Elaborando solicitação de férias"),
  10306: montarFamiliaAvulso("Elaborando alteração de dependente"),
  10373: montarFamiliaAvulso("Elaborando alteração de pró-labore"),
};

const TITULOS_POR_TIPO = {
  1: "Abertura de Empresa",
  3: "Migração de Empresa",
  4: "Alteração de Empresa",
  5: "Baixa/Encerramento de Empresa",
  2: "Serviço de Contabilidade",
  101: "Serviço Avulso Contábil",
  102: "Serviço Avulso Fiscal",
  103: "Serviço Avulso de RH",
  106: "Serviço Avulso",
  10301: "Admissão de Empregado",
  10302: "Rescisão de Empregado",
  10304: "Solicitação de Férias",
  10306: "Alteração de Dependente",
  10373: "Alteração de Valor de Pró-labore",
};

export function familiaPorTipo(tipoId) {
  return FAMILIAS_POR_TIPO[tipoId] || FAMILIAS_POR_TIPO[String(tipoId)];
}

export function tituloPorTipo(tipoId) {
  return TITULOS_POR_TIPO[tipoId] || TITULOS_POR_TIPO[String(tipoId)] || "Andamento da Ordem de Serviço";
}

/**
 * Monta a lista de nós visuais (na ordem cronológica da família) e marca o
 * status de cada um (concluída / atual / pendente) a partir da situação em
 * que a OS está agora.
 *
 * @param {boolean} comConselho - quando true, insere a etapa "Protocolo
 *   Conselho de Classe" (C-CCM) logo após a análise municipal.
 */
export function montarTrilha(tipoId, situacaoIdAtual, comConselho = false) {
  const familia = familiaPorTipo(tipoId);
  if (!familia) return [];

  let ordem = familia.ordem;
  if (comConselho && ordem.includes("A-CCM") && !ordem.includes("C-CCM")) {
    const posicao = ordem.indexOf("A-CCM") + 1;
    ordem = [...ordem.slice(0, posicao), "C-CCM", ...ordem.slice(posicao)];
  }

  const indiceSituacaoAtual = ordem.indexOf(situacaoIdAtual);

  const nosMontados = [];
  ordem.forEach((codigo, indiceSituacao) => {
    const definicoes =
      familia.nos[codigo] || (codigo === "C-CCM" ? [{ label: "Certidão Conselho de Classe" }] : []);

    definicoes.forEach((def, posicaoNoGrupo) => {
      let status = "pendente";
      if (indiceSituacaoAtual !== -1) {
        if (indiceSituacao < indiceSituacaoAtual) {
          status = "concluida";
        } else if (indiceSituacao === indiceSituacaoAtual) {
          // dentro do grupo da situação atual, só o ÚLTIMO nó fica "atual";
          // os anteriores do mesmo grupo já contam como feitos.
          status = posicaoNoGrupo === definicoes.length - 1 ? "atual" : "concluida";
        }
      }
      nosMontados.push({ ...def, status, chave: `${codigo}-${posicaoNoGrupo}` });
    });
  });

  return nosMontados;
}
